import { router } from "@inertiajs/react";
import { CompetitorAnalysis } from "@/bizminer/App/PillarInsights/Swot/_components/CompetitorAnalysis.tsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { competitor_bizminer_app_pillar_insight_swot_path } from "@/routes";
import type { BizminerPillarInsight } from "@/types/serializers";

export interface CompetitorTabType {
  value: string;
  label: string;
  competitor: BizminerPillarInsight["swot"][number] & {
    name: string;
  };
}

export function SwotTabs({
  swotData,
  currentCompetitor,
  competitors,
  pillarInsightId,
  accountName,
}: {
  swotData: BizminerPillarInsight["swot"];
  currentCompetitor?: string;
  competitors: BizminerPillarInsight["competitors"];
  pillarInsightId: number;
  accountName: string;
}) {
  // Map swotData (which contains competitor IDs) to full competitor data
  const competitorTabs = swotData.map((swotItem) => {
    const competitor = competitors.find(
      (c: { id: string }) => c.id === swotItem.competitor_id,
    );
    return {
      value: swotItem.competitor_id,
      label: competitor?.name || "Unknown Competitor",
      competitor: {
        ...swotItem,
        name: competitor?.name || "Unknown Competitor",
      },
    };
  });

  // If no valid competitor is set, default to first competitor
  const validTab =
    currentCompetitor &&
    competitorTabs.find((tab) => tab.value === currentCompetitor)
      ? currentCompetitor
      : competitorTabs[0]?.value;

  // Handle tab changes with URL updates
  const handleTabChange = (value: string) => {
    router.visit(
      competitor_bizminer_app_pillar_insight_swot_path(pillarInsightId, value),
      {
        preserveScroll: true,
        preserveState: false,
        replace: false,
      },
    );
  };

  return (
    <Tabs
      value={validTab}
      onValueChange={handleTabChange}
      className="space-y-6"
    >
      <div className="relative -mx-4 px-4 overflow-x-auto scrollbar-thin">
        <TabsList className="w-max min-w-max justify-start">
          {competitorTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="flex-none"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {competitorTabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          <CompetitorAnalysis
            competitor={tab.competitor}
            accountName={accountName}
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}
